/**
 * Display-name rules for signup and profile rename.
 *
 * Length is Unicode code points, counted by walking the string, matching
 * Python `len()`. `String.length` and HTML `maxLength` count UTF-16 units.
 */

export const MAX_DISPLAY_NAME_LENGTH = 32;

const HIDDEN_MESSAGE = "Username can't include hidden or control characters.";
const REQUIRED_MESSAGE = 'Username is required.';
const TOO_LONG_MESSAGE = `Username must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer.`;

/** Unicode Cc (control) and Cf (format), matching Python `unicodedata.category`. */
const HIDDEN_CHAR = /\p{Cc}|\p{Cf}/u;

export interface FirebaseNameSource {
  displayName?: string | null;
  email?: string | null;
}

export type DisplayNameCheck =
  | { ok: true; value: string }
  | { ok: false; error: string };

function hasHiddenChars(value: string): boolean {
  return HIDDEN_CHAR.test(value);
}

function normalize(value: string): string {
  return value.normalize('NFC').replace(/\s+/g, ' ').trim();
}

export function codePointLength(value: string): number {
  // Walk code points directly. Spreading a string needs
  // `downlevelIteration`, which this project's ES5 target does not enable.
  let count = 0;
  for (let i = 0; i < value.length; ) {
    const code = value.codePointAt(i) ?? 0;
    count += 1;
    i += code > 0xffff ? 2 : 1;
  }
  return count;
}

/** Profile name, or '' while that load is pending, otherwise Firebase or `fallback`. */
export function getDisplayName({
  profileName,
  firebaseUser,
  fallback,
  profilePending = false,
}: {
  profileName?: string | null;
  firebaseUser?: FirebaseNameSource | null;
  fallback: string;
  profilePending?: boolean;
}): string {
  const fromProfile = profileName?.trim();
  if (fromProfile) return fromProfile;
  if (profilePending) return '';
  const fromFirebase =
    firebaseUser?.displayName?.trim() || firebaseUser?.email?.trim();
  return fromFirebase || fallback;
}

/** Required name (rename). Empty and over-long values are errors. */
export function validateDisplayName(raw: string): DisplayNameCheck {
  const normalized = raw.normalize('NFC');
  if (hasHiddenChars(normalized)) {
    return { ok: false, error: HIDDEN_MESSAGE };
  }
  const cleaned = normalize(normalized);
  const length = codePointLength(cleaned);
  if (length < 1) return { ok: false, error: REQUIRED_MESSAGE };
  if (length > MAX_DISPLAY_NAME_LENGTH) {
    return { ok: false, error: TOO_LONG_MESSAGE };
  }
  return { ok: true, value: cleaned };
}

/**
 * Signup username. Blank (after trimming) means "use my email".
 * A name made only of hidden characters is still rejected.
 */
export function validateOptionalUsername(
  raw: string
): { ok: true; value: string | null } | { ok: false; error: string } {
  const normalized = raw.normalize('NFC');
  if (hasHiddenChars(normalized)) {
    return { ok: false, error: HIDDEN_MESSAGE };
  }
  const cleaned = normalize(normalized);
  if (codePointLength(cleaned) === 0) return { ok: true, value: null };
  const checked = validateDisplayName(cleaned);
  if (!checked.ok) return checked;
  return { ok: true, value: checked.value };
}
